import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Alert, Spin, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { API } from '@/services/api.service';
import { formatAmount, formatRate } from '../format';

const { Text } = Typography;

/**
 * Before publishing: foreign-currency accounts holding lines booked in dinars
 * alone, which leave the dinar balance off the currency balance at the NBS
 * middle rate, and balances not revalued to the month-end rate. Only a
 * warning — the correction is booked in AB Soft.
 *
 * @param {Object} props - Component props
 * @param {string} props.companyId - Company
 * @param {number} props.year - Year being published
 * @param {number} props.month - Month being published
 * @returns {JSX.Element|null} The warning, or nothing when all is in order
 */
export default function FxCheckAlert({ companyId, year, month }) {
  const { t } = useTranslation('clientReports');
  const { data, isLoading } = useQuery({
    queryKey: ['client-reports-fx-check', companyId, year, month],
    queryFn: () => API.clientReports.getFxCheck(companyId, { year, month }),
  });

  if (isLoading) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Spin size="small" />{' '}
        <Text type="secondary">{t('release.fxCheck.loading')}</Text>
      </div>
    );
  }
  const check = data?.data;
  if (!check) return null;

  const missing = check.missingRates?.length > 0 && (
    <Alert
      type="info"
      showIcon
      style={{ marginBottom: 12 }}
      message={t('release.fxCheck.missingRates', {
        currencies: check.missingRates.join(', '),
      })}
    />
  );
  const unrevalued = check.unrevalued || [];
  if (!check.groups.length && !unrevalued.length) return missing || null;

  const columns = [
    {
      title: t('release.fxCheck.account'),
      key: 'account',
      render: (_, g) => (
        <>
          <div>{g.account}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {g.accountName}
          </Text>
        </>
      ),
    },
    {
      title: t('release.fxCheck.partner'),
      key: 'partner',
      render: (_, g) =>
        g.partner ? (
          <>
            <div>{g.partner}</div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {g.partnerName}
            </Text>
          </>
        ) : (
          '—'
        ),
    },
    {
      title: t('release.fxCheck.inCurrency'),
      key: 'balances',
      align: 'right',
      render: (_, g) =>
        g.balances.map((b) => (
          <div
            key={b.currency}
          >{`${formatAmount(b.amount)} ${b.currency}`}</div>
        )),
    },
    {
      title: t('release.fxCheck.gap'),
      dataIndex: 'gap',
      key: 'gap',
      align: 'right',
      render: (v) => formatAmount(v),
    },
  ];

  const lineColumns = [
    {
      title: t('release.fxCheck.date'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (v) => dayjs(v).format('DD.MM.YYYY'),
    },
    { title: t('release.fxCheck.order'), dataIndex: 'order', key: 'order' },
    {
      title: t('release.fxCheck.description'),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t('release.fxCheck.amount'),
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (v) => formatAmount(v),
    },
  ];

  const unrevaluedColumns = [
    columns[0],
    columns[1],
    columns[2],
    {
      title: t('release.fxCheck.bookRate'),
      dataIndex: 'bookRate',
      key: 'bookRate',
      align: 'right',
      render: formatRate,
    },
    {
      title: t('release.fxCheck.nbsRate'),
      dataIndex: 'nbsRate',
      key: 'nbsRate',
      align: 'right',
      render: formatRate,
    },
    columns[3],
  ];

  return (
    <>
      {missing}
      {unrevalued.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('release.fxCheck.unrevaluedTitle', {
            count: unrevalued.length,
            date: dayjs(check.date).format('DD.MM.YYYY'),
          })}
          description={
            <>
              <div style={{ marginBottom: 8 }}>
                {t('release.fxCheck.unrevaluedHint')}
              </div>
              <Table
                size="small"
                scroll={{ x: 'max-content' }}
                rowKey={(g) => `${g.account}|${g.partner}`}
                columns={unrevaluedColumns}
                dataSource={unrevalued}
                pagination={false}
              />
            </>
          }
        />
      )}
      {check.groups.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('release.fxCheck.title', {
            count: check.groups.length,
            date: dayjs(check.date).format('DD.MM.YYYY'),
          })}
          description={
            <>
              <div style={{ marginBottom: 8 }}>{t('release.fxCheck.hint')}</div>
              <Table
                size="small"
                scroll={{ x: 'max-content' }}
                rowKey={(g) => `${g.account}|${g.partner}`}
                columns={columns}
                dataSource={check.groups}
                pagination={false}
                expandable={{
                  expandedRowRender: (g) => (
                    <Table
                      size="small"
                      scroll={{ x: 'max-content' }}
                      rowKey={(l, i) => `${l.order}-${i}`}
                      columns={lineColumns}
                      dataSource={g.lines}
                      pagination={false}
                    />
                  ),
                }}
              />
            </>
          }
        />
      )}
    </>
  );
}
